import { useState } from 'react';
import { PRIORITAET_NAMEN, STATUS_NAMEN, istUeberfaellig, type Aufgabe, type MindmapAuswahl } from '@beholder/shared/module/aufgaben';
import { api } from '../../core/api';
import { Knopf, Meldung, datumZeit, useAktion } from '../../core/ui';
import { faelligText, kuerzel, pfad, ueberfaelligText } from './daten';

export function StatusKnopf({
  a,
  projektId,
  darfSchreiben,
  geaendert,
}: {
  a: Aufgabe;
  projektId: string;
  darfSchreiben: boolean;
  geaendert(a: Aufgabe): void;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const erledigt = a.status === 'erledigt';
  const text = erledigt ? 'Wieder öffnen' : 'Als erledigt markieren';
  return (
    <button
      type="button"
      className={`auf-haken auf-haken-${a.status}`}
      disabled={!darfSchreiben || laeuft}
      aria-label={darfSchreiben ? text : STATUS_NAMEN[a.status]}
      title={darfSchreiben ? text : STATUS_NAMEN[a.status]}
      onClick={async (e) => {
        e.stopPropagation();
        setLaeuft(true);
        try {
          geaendert(await api.patch<Aufgabe>(pfad(projektId, `/${a.id}`), { status: erledigt ? 'offen' : 'erledigt' }));
        } catch (f) {
          alert(f instanceof Error ? f.message : 'Fehler');
        } finally {
          setLaeuft(false);
        }
      }}
    >
      {erledigt ? '✓' : ''}
    </button>
  );
}

export function Personen({ personen, ichId }: { personen: { id: string; name: string }[]; ichId: string }) {
  if (!personen.length) return null;
  return (
    <span className="auf-personen" aria-label={`Zugewiesen: ${personen.map((p) => p.name).join(', ')}`}>
      {personen.slice(0, 4).map((p) => (
        <span key={p.id} className={`auf-person${p.id === ichId ? ' auf-ich' : ''}`} title={p.name}>
          {kuerzel(p.name)}
        </span>
      ))}
      {personen.length > 4 && <span className="auf-person auf-mehr">+{personen.length - 4}</span>}
    </span>
  );
}

export function AufgabenZeile({
  a,
  ichId,
  projektId,
  darfSchreiben,
  gewaehlt,
  oeffnen,
  geaendert,
  mindmap,
}: {
  a: Aufgabe;
  ichId: string;
  projektId: string;
  darfSchreiben: boolean;
  gewaehlt: boolean;
  oeffnen(): void;
  geaendert(a: Aufgabe): void;
  mindmap: MindmapAuswahl;
}) {
  const ueber = istUeberfaellig(a);
  const fertig = a.punkte.filter((p) => p.erledigt).length;
  const knoten = a.mindmapKnotenId && mindmap.verfuegbar ? mindmap.knoten.find((k) => k.id === a.mindmapKnotenId) : undefined;
  return (
    <li
      className={`auf-zeile${ueber ? ' auf-ueberfaellig' : ''}${a.status === 'erledigt' ? ' auf-erledigt' : ''}${gewaehlt ? ' auf-gewaehlt' : ''}`}
    >
      <StatusKnopf a={a} projektId={projektId} darfSchreiben={darfSchreiben} geaendert={geaendert} />
      <button type="button" className="auf-zeile-inhalt" onClick={oeffnen}>
        <span className="auf-zeile-titel">{a.titel}</span>
        <span className="auf-zeile-marken">
          {a.status === 'in_arbeit' && <span className="auf-marke auf-marke-arbeit">In Arbeit</span>}
          {a.status === 'wartet' && <span className="auf-marke auf-marke-wartet">Wartet</span>}
          {a.prioritaet === 'hoch' && <span className="auf-marke auf-marke-hoch">{PRIORITAET_NAMEN.hoch}</span>}
          {a.prioritaet === 'niedrig' && <span className="auf-marke">{PRIORITAET_NAMEN.niedrig}</span>}
          {a.faellig && (
            <span className={`auf-marke${ueber ? ' auf-marke-ueber' : ''}`} title={ueber ? (ueberfaelligText(a) ?? '') : 'fällig'}>
              {ueber ? `⚠ ${ueberfaelligText(a)}` : `fällig ${faelligText(a.faellig)}`}
            </span>
          )}
          {a.punkte.length > 0 && (
            <span className="auf-marke" title="Checkliste">
              ☑ {fertig}/{a.punkte.length}
            </span>
          )}
          {a.anlagenAnzahl > 0 && (
            <span className="auf-marke" title="Anlagen">
              📎 {a.anlagenAnzahl}
            </span>
          )}
          {a.kommentarAnzahl > 0 && (
            <span className="auf-marke" title="Kommentare">
              💬 {a.kommentarAnzahl}
            </span>
          )}
          {knoten && (
            <span className="auf-marke" title={knoten.pfad}>
              ◎ {knoten.titel}
            </span>
          )}
        </span>
      </button>
      <Personen personen={a.zugewiesen} ichId={ichId} />
    </li>
  );
}

export function VorschlagKarte({
  a,
  projektId,
  darfSchreiben,
  gewaehlt,
  oeffnen,
  geaendert,
  weg,
}: {
  a: Aufgabe;
  projektId: string;
  darfSchreiben: boolean;
  gewaehlt: boolean;
  oeffnen(): void;
  geaendert(a: Aufgabe): void;
  weg(id: string): void;
}) {
  const aktion = useAktion();
  return (
    <article className={`auf-vorschlag${gewaehlt ? ' auf-gewaehlt' : ''}`}>
      <button type="button" className="auf-vorschlag-kopf" onClick={oeffnen}>
        <strong>{a.titel}</strong>
        <span className="leise klein">
          {a.herkunft || 'Vorschlag'}
          {a.erstelltVon ? ` · von ${a.erstelltVon.name}` : ''} · {datumZeit(a.erstelltAm)}
        </span>
      </button>
      {a.beschreibung && <p className="auf-vorschlag-text zeilen-2">{a.beschreibung}</p>}
      {(a.zugewiesen.length > 0 || a.faellig) && (
        <p className="leise klein">
          {a.zugewiesen.length > 0 && `Für ${a.zugewiesen.map((p) => p.name).join(', ')}`}
          {a.zugewiesen.length > 0 && a.faellig && ' · '}
          {a.faellig && `fällig ${faelligText(a.faellig)}`}
        </p>
      )}
      <Meldung>{aktion.fehler}</Meldung>
      {darfSchreiben && (
        <div className="reihe">
          <Knopf
            art="haupt"
            klein
            disabled={aktion.laeuft}
            onClick={async () => {
              const neu = await aktion.ausfuehren(() => api.post<Aufgabe>(pfad(projektId, `/${a.id}/annehmen`)));
              if (neu) geaendert(neu);
            }}
          >
            Übernehmen
          </Knopf>
          <Knopf
            klein
            disabled={aktion.laeuft}
            onClick={async () => {
              if (!confirm(`Vorschlag „${a.titel}“ verwerfen? Er liegt danach 180 Tage im Papierkorb.`)) return;
              const ok = await aktion.ausfuehren(() => api.post(pfad(projektId, `/${a.id}/verwerfen`)));
              if (ok) weg(a.id);
            }}
          >
            Verwerfen
          </Knopf>
          <Knopf art="leise" klein onClick={oeffnen}>
            Ansehen / bearbeiten
          </Knopf>
        </div>
      )}
    </article>
  );
}
